import { useEffect, useMemo, type ReactElement } from 'react';
import { Box, Flex, Group, Loader, Stack, Text } from '@mantine/core';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import TopicSummaryPanel from '@/shared/surfaces/TopicSummaryPanel';
import TopicInsightsPanel from '@/shared/surfaces/TopicInsightsPanel';
import { deriveCallouts } from '@/shared/surfaces/TopicNarration';
import type { TopicDefinition } from '@/shared/surfaces/TopicCatalogTypes';
import type { AnalyticsField, AnalyticsFilter } from '@/shared/analytics/AnalyticsTypes';
import type { StatItem } from '@/design/StatBand';
import { formatCount, formatPercent, metricLabel } from '@/shared/analytics/MetricFormatting';
import { UseCaseBreakdownViewModel, type UseCaseBreakdownRow } from './UseCaseBreakdownViewModel';
import UseCaseRankedList from './UseCaseRankedList';

interface UseCaseBreakdownSectionProps {
    /** The classifier field to break down by. */
    field: AnalyticsField;
    /** Server-side scope (slice + ambient); memoize at the call site. */
    filters: AnalyticsFilter[];
    /** Join each value to its usage-category taxonomy root as a dimmed subline. */
    withParents: boolean;
    /** When set, preload this field grouped under each row so rows expand to reveal nested children. */
    childField?: AnalyticsField;
    /** Backs the narration and LLM-insights panels. */
    definition: TopicDefinition;
    surfaceTitle: string;
    /** Column header for the value column, e.g. `Category`. */
    lensHeader: string;
    /** The click-through hint shown beside the section label. */
    hint: string;
    hrefFor?: (row: UseCaseBreakdownRow) => string;
    onRowClick?: (row: UseCaseBreakdownRow) => void;
}

/**
 * The ranked breakdown with its side rail: the classic ranked-bars list of a
 * classifier field beside the Summary and LLM Insights cards — shared by the
 * Use Cases landing (task categories) and the category page's Subcategories
 * tab (specific tasks).
 */
export default function UseCaseBreakdownSection({ field, filters, withParents, childField, definition, surfaceTitle, lensHeader, hint, hrefFor, onRowClick }: UseCaseBreakdownSectionProps): ReactElement {
    const vm = useViewModel(UseCaseBreakdownViewModel);

    useEffect(() => {
        void vm.load(field, filters, withParents, childField);
    }, [vm, field, filters, withParents, childField]);

    const callouts = useMemo(() => (vm.primary === null ? [] : deriveCallouts(definition, vm.primary)), [definition, vm.primary]);

    if (vm.errorMessage !== null) {
        return (
            <Text size="sm" c="red">
                {vm.errorMessage}
            </Text>
        );
    }
    if (vm.loading && vm.rows.length === 0) {
        return (
            <Group justify="center" gap="sm" py="xl">
                <Loader size="sm" />
                <Text size="sm" c="dimmed">
                    Loading breakdown…
                </Text>
            </Group>
        );
    }

    const capitalLens = definition.lensPlural.charAt(0).toUpperCase() + definition.lensPlural.slice(1);
    const totals = vm.primary?.totals;
    const stats: StatItem[] =
        vm.primary === null || totals === undefined ?
            []
        :   [
                { label: metricLabel('turns'), value: formatCount(totals.turns) },
                { label: metricLabel('sessions'), value: formatCount(totals.sessions) },
                { label: 'Users', value: formatCount(totals.users) },
                { label: capitalLens, value: formatCount(vm.primary.distinctCount) },
                { label: 'Avg confidence', value: formatPercent(totals.avgConfidence) },
            ];

    return (
        <Flex gap="lg" align="flex-start" direction={{ base: 'column', lg: 'row' }}>
            <Box style={{ flex: 1, minWidth: 0, width: '100%' }}>
                <Stack gap="sm">
                    <Group justify="space-between" align="baseline">
                        <Text size="sm" fw={700} style={{ letterSpacing: '-0.02em' }}>
                            By {definition.lensLabel}
                        </Text>
                        <Text size="xs" c="dimmed">
                            {hint}
                        </Text>
                    </Group>
                    <UseCaseRankedList rows={vm.rows} lensHeader={lensHeader} lensPlural={definition.lensPlural} showParents={withParents} hrefFor={hrefFor} onRowClick={onRowClick} />
                </Stack>
            </Box>
            <Box style={{ width: 300, flexShrink: 0, maxWidth: '100%' }}>
                <Stack gap="md">
                    <TopicSummaryPanel stats={stats} />
                    {vm.primary !== null && <TopicInsightsPanel surfaceTitle={surfaceTitle} definition={definition} primary={vm.primary} derived={callouts} />}
                </Stack>
            </Box>
        </Flex>
    );
}
